import { useCallback, useEffect, useRef, useState } from 'react';

/** How long "Copied" or "Copy failed" stays before the control reads "Copy" again. */
const RESET_AFTER_MS = 1500;

export type CopyState = 'idle' | 'copied' | 'failed';

/**
 * Copy text to the clipboard and say whether it worked (spec 096 FR-008).
 *
 * One hook rather than a `try/catch` at each button, because the failure is the
 * part that matters: a refused clipboard — an insecure origin, a denied permission,
 * a browser without the API — is silent, and a control that goes back to "Copy" as
 * if nothing happened has told the reader their text is on the clipboard when it
 * is not. `failed` is what lets every caller say so in the same words.
 */
export function useCopyToClipboard(): {
  /** Resolves whether the text reached the clipboard (spec 114), for a caller
      that reports the outcome somewhere other than `state` — a toast. */
  copy: (text: string) => Promise<boolean>;
  state: CopyState;
} {
  const [state, setState] = useState<CopyState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(async (text: string) => {
    if (timer.current) clearTimeout(timer.current);
    let next: CopyState;
    try {
      await navigator.clipboard.writeText(text);
      next = 'copied';
    } catch {
      next = 'failed';
    }
    setState(next);
    timer.current = setTimeout(() => {
      setState('idle');
    }, RESET_AFTER_MS);
    return next === 'copied';
  }, []);

  return { copy, state };
}
